/**
 * AssistFormBridge — ponte entre páginas com formulário e o VUKZ Assist
 * global (Single Assist, 26/08/2026) + Assist Form Contract v1 (12/09/2026).
 *
 * ARQUIVO SEPARADO DE PROPÓSITO: não importa react-router-dom nem nenhum
 * contexto pesado — o jest do CRA não resolve react-router-dom v7 (ESM), e
 * páginas testadas (TrainingBuilderWizard etc.) importam este módulo
 * estaticamente.
 *
 * Contrato do descriptor registrado (campos legados continuam válidos):
 *   {
 *     formId: 'training_builder',
 *     label: 'Criar Treino',
 *     schemaVersion: 'v1',
 *     getFormContext: () => form_context,   // estado FRESCO em tempo de envio
 *     applyPatch: (patch, fillForm) => ({ applied, rejected }),   // legado
 *     applySelection?: (field, option) => boolean,  // request_selection
 *
 *     // ── Contract v1 (opcionais; sem eles o form segue no fluxo legado) ──
 *     formInstanceId?: 'training_plan_editor:441',
 *     capabilities?: { ...legado, set, add, remove, replace, reorder, selectEntity },
 *     getRevisionSource?: () => estado RELEVANTE (a Bridge faz o fingerprint),
 *     getRevision?: () => revision real quando existir (draft_revision etc.),
 *     getFocus?: () => { path, value, label, entity?, focusRevision } | null,
 *     toOperations?: (patch) => { operations, rejected, entity },   // compat específico
 *     applyOperations?: (operations, meta) => {
 *        applied:[operationId], rejected:[{operationId,code,reason}],
 *        inverseOperations:[op], nextRevisionSource?:any, warnings?:[] },
 *     resolvePath?: (path, entity) => valor ATUAL (para o diff real),
 *   }
 *
 * A Bridge NÃO conhece regras de Coach/Prep/Nutri: só identifica o form
 * ativo, captura contexto no send(), avalia staleness, encaminha ao
 * adapter e guarda o histórico de aplicação (undo) por formInstanceId.
 *
 * useAssistFormBridge é TOLERANTE: fora do provider devolve no-ops —
 * páginas renderizam em testes sem Assist.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { fingerprint } from '../components/assist/contract/fingerprint';
import {
  PROPOSAL_STATUS, ERROR_CODES, buildProposal, evaluateStaleness,
} from '../components/assist/contract/proposalContract';
import { fillFormToOperations } from '../components/assist/contract/compatibilityAdapter';

const AssistFormBridgeContext = createContext(null);

const NOOP_BRIDGE = {
  registerAssistForm: () => {},
  unregisterAssistForm: () => {},
  getActiveForm: () => null,
  activeFormId: null,
  // Contract v1
  getRevision: () => null,
  getFocus: () => null,
  currentGeneration: () => 0,
  captureSendContext: () => null,
  supersedePending: () => 0,
  proposalFromFillForm: () => null,
  evaluateProposal: () => ({ stale: true, reason: ERROR_CODES.FORM_NOT_ACTIVE }),
  resolveCurrentValue: () => undefined,
  applyProposal: () => ({ status: 'rejected', code: ERROR_CODES.FORM_NOT_ACTIVE }),
  undoLastAppliedProposal: () => ({ status: 'rejected', code: ERROR_CODES.UNDO_NOT_AVAILABLE }),
  canUndo: () => false,
  notifyCanonicalSave: () => {},
  supportsContract: () => false,
};

export const useAssistFormBridge = () => {
  const ctx = useContext(AssistFormBridgeContext);
  return ctx || NOOP_BRIDGE;
};

// Identidade da instância — resolução DINÂMICA e retrocompatível:
// getFormInstanceId() (entidade realmente editada) → formInstanceId
// estático → formId. Um único descriptor ativo; só a identidade acompanha
// o alvo da edição. Usado em captura, stale, histórico, undo e save.
const instanceOf = (desc) => {
  if (!desc) return null;
  if (typeof desc.getFormInstanceId === 'function') {
    try { const v = desc.getFormInstanceId(); if (v != null) return String(v); } catch (e) { /* cai no estático */ }
  }
  return desc.formInstanceId || (desc.formId ? `${desc.formId}:*` : null);
};

export const AssistFormBridgeProvider = ({ children }) => {
  // Formulário ativo em ref (getFormContext/applyPatch mudam a cada render
  // da página); state só com o formId para o shell reagir.
  const activeFormRef = useRef(null);
  const [activeFormId, setActiveFormId] = useState(null);
  // Contract v1: geração de interação (v1 conservador: todo send() supersede
  // propostas pendentes) e histórico de aplicação por formInstanceId.
  const generationRef = useRef(0);
  const historyRef = useRef(new Map()); // formInstanceId → AppliedProposalRecord[]

  const registerAssistForm = useCallback((descriptor) => {
    if (!descriptor?.formId) return;
    const prevInstance = instanceOf(activeFormRef.current);
    activeFormRef.current = descriptor;
    // Trocar de instância invalida propostas em voo (SUPERSEDED via geração)
    if (prevInstance && prevInstance !== instanceOf(descriptor)) generationRef.current += 1;
    setActiveFormId((prev) => (prev === descriptor.formId ? prev : descriptor.formId));
  }, []);

  const unregisterAssistForm = useCallback((formId) => {
    if (activeFormRef.current?.formId === formId) {
      activeFormRef.current = null;
      generationRef.current += 1;
      setActiveFormId(null);
    }
  }, []);

  const getActiveForm = useCallback(() => activeFormRef.current, []);
  const supportsContract = useCallback(() => typeof activeFormRef.current?.applyOperations === 'function', []);

  // ── Revision: real quando o adapter oferece; senão fingerprint ÚNICO da
  // Bridge sobre o revisionSource do domínio (nunca hashing por adapter).
  const getRevision = useCallback(() => {
    const d = activeFormRef.current;
    if (!d) return null;
    if (typeof d.getRevision === 'function') {
      const r = d.getRevision();
      if (r != null) return String(r);
    }
    const src = typeof d.getRevisionSource === 'function'
      ? d.getRevisionSource()
      : (typeof d.getFormContext === 'function' ? d.getFormContext() : null);
    return src == null ? null : fingerprint(src);
  }, []);

  const getFocus = useCallback(() => {
    const d = activeFormRef.current;
    try { return typeof d?.getFocus === 'function' ? (d.getFocus() || null) : null; }
    catch (e) { return null; }
  }, []);

  const currentGeneration = useCallback(() => generationRef.current, []);
  const supersedePending = useCallback(() => { generationRef.current += 1; return generationRef.current; }, []);

  /**
   * Captura NO send(): formId, instância, baseRevision, foco e geração.
   * v1: cada send incrementa a geração (novo turno supersede pendentes).
   * NUNCA recalcular baseRevision na chegada da resposta.
   */
  const captureSendContext = useCallback((extra = {}) => {
    const d = activeFormRef.current;
    if (!d) return null;
    generationRef.current += 1;
    return {
      formId: d.formId,
      formInstanceId: instanceOf(d),
      baseRevision: getRevision(),
      focus: getFocus(),
      interactionGeneration: generationRef.current,
      sentAt: new Date().toISOString(),
      ...extra,
    };
  }, [getRevision, getFocus]);

  const activeSnapshot = useCallback(() => {
    const d = activeFormRef.current;
    if (!d) return null;
    return {
      formId: d.formId,
      formInstanceId: instanceOf(d),
      revision: getRevision(),
      interactionGeneration: generationRef.current,
    };
  }, [getRevision]);

  /** fill_form legado → Proposal v1 (usa o contexto CAPTURADO no send). */
  const proposalFromFillForm = useCallback((fillForm, captured) => {
    const d = activeFormRef.current;
    if (!captured || !fillForm?.patch) return null;
    // O descriptor que ORIGINOU a captura pode não ser mais o ativo — ainda
    // assim montamos a proposta (será STALE por instância/geração).
    const conv = (d && d.formId === captured.formId && typeof d.toOperations === 'function')
      ? d.toOperations(fillForm.patch)
      : fillFormToOperations(fillForm.patch, { source: captured.inputSource || 'conversation' });
    const proposal = buildProposal({
      captured,
      operations: conv.operations,
      entity: conv.entity || null,
      warnings: [...(fillForm.warnings || []), ...conv.rejected.map((r) => `${r.path}: ${r.reason}`)],
      missingFields: fillForm.missingFields || [],
      final: fillForm.final,
    });
    if (proposal) proposal.compatRejected = conv.rejected;
    return proposal;
  }, []);

  const evaluateProposal = useCallback((proposal) => evaluateStaleness(proposal, activeSnapshot()), [activeSnapshot]);

  /** Valor ATUAL do path (para diff real) — nunca o current_value do LLM. */
  const resolveCurrentValue = useCallback((path, entity) => {
    const d = activeFormRef.current;
    if (!d || typeof d.resolvePath !== 'function') return undefined;
    try { return d.resolvePath(path, entity); } catch (e) { return undefined; }
  }, []);

  /**
   * Apply: checks de contrato (form, instância, revisão, geração, já
   * aplicada) → adapter.applyOperations(aceitas) → histórico + inversas.
   * NUNCA chama API: Apply ≠ Save.
   */
  const applyProposal = useCallback((proposal, { acceptedOperationIds = null } = {}) => {
    const d = activeFormRef.current;
    const staleness = evaluateStaleness(proposal, activeSnapshot());
    if (staleness.stale) {
      if (staleness.reason !== ERROR_CODES.PROPOSAL_ALREADY_APPLIED && staleness.reason !== ERROR_CODES.REJECTED_BY_USER) {
        proposal.status = PROPOSAL_STATUS.STALE; proposal.staleReason = staleness.reason;
      }
      return { proposalId: proposal.proposalId, status: 'stale', code: staleness.reason, appliedOperations: [], rejectedOperations: [] };
    }
    if (typeof d.applyOperations !== 'function') {
      return { proposalId: proposal.proposalId, status: 'rejected', code: ERROR_CODES.OPERATION_NOT_ALLOWED, appliedOperations: [], rejectedOperations: [] };
    }
    const accepted = acceptedOperationIds
      ? proposal.operations.filter((op) => acceptedOperationIds.includes(op.operationId))
      : proposal.operations;
    const rejectedByUser = proposal.operations
      .filter((op) => !accepted.includes(op))
      .map((op) => ({ operationId: op.operationId, code: ERROR_CODES.REJECTED_BY_USER }));
    if (accepted.length === 0) {
      proposal.status = PROPOSAL_STATUS.REJECTED;
      return { proposalId: proposal.proposalId, status: 'rejected', code: ERROR_CODES.REJECTED_BY_USER, appliedOperations: [], rejectedOperations: rejectedByUser };
    }
    const revisionBefore = getRevision();
    const result = d.applyOperations(accepted, { proposal, entity: proposal.entity, undo: false }) || {};
    const applied = result.applied || [];
    const rejected = [...(result.rejected || []), ...rejectedByUser];
    if (applied.length === 0) {
      return { proposalId: proposal.proposalId, status: 'rejected', code: rejected[0]?.code || ERROR_CODES.DOMAIN_VALIDATION_FAILED, appliedOperations: [], rejectedOperations: rejected, warnings: result.warnings || [] };
    }
    const revisionAfter = result.nextRevisionSource !== undefined ? fingerprint(result.nextRevisionSource) : null;
    const instance = instanceOf(d);
    const record = {
      proposalId: proposal.proposalId,
      formInstanceId: instance,
      revisionBefore,
      revisionAfter,
      appliedOperations: applied,
      inverseOperations: result.inverseOperations || [],
      appliedAt: new Date().toISOString(),
      status: PROPOSAL_STATUS.APPLIED,
      entity: proposal.entity || null,
    };
    const list = historyRef.current.get(instance) || [];
    list.push(record);
    historyRef.current.set(instance, list);
    // Aplicar muda a revisão → propostas concorrentes com base antiga ficam STALE
    generationRef.current += 1;
    const partial = applied.length < proposal.operations.length;
    proposal.status = partial ? PROPOSAL_STATUS.PARTIALLY_APPLIED : PROPOSAL_STATUS.APPLIED;
    return {
      proposalId: proposal.proposalId,
      status: partial ? 'partially_applied' : 'applied',
      revisionBefore, revisionAfter,
      appliedOperations: applied, rejectedOperations: rejected, warnings: result.warnings || [],
    };
  }, [activeSnapshot, getRevision]);

  const lastUndoable = useCallback((formInstanceId) => {
    const inst = formInstanceId || instanceOf(activeFormRef.current);
    const list = historyRef.current.get(inst) || [];
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].status === PROPOSAL_STATUS.APPLIED && list[i].inverseOperations.length > 0) return list[i];
    }
    return null;
  }, []);

  const canUndo = useCallback((formInstanceId) => !!lastUndoable(formInstanceId), [lastUndoable]);

  /**
   * Undo: executa as inversas JÁ ARMAZENADAS (nunca pergunta ao LLM). Só
   * atua sobre a instância ativa; se o formulário mudou depois do apply
   * (edição humana), não sobrescreve → UNDO_NOT_AVAILABLE.
   */
  const undoLastAppliedProposal = useCallback((formInstanceId) => {
    const d = activeFormRef.current;
    const inst = instanceOf(d);
    if (!d || (formInstanceId && formInstanceId !== inst)) {
      return { status: 'rejected', code: ERROR_CODES.FORM_INSTANCE_CHANGED };
    }
    const record = lastUndoable(inst);
    if (!record || typeof d.applyOperations !== 'function') return { status: 'rejected', code: ERROR_CODES.UNDO_NOT_AVAILABLE };
    const current = getRevision();
    if (record.revisionAfter && current && record.revisionAfter !== current) {
      return { status: 'rejected', code: ERROR_CODES.UNDO_NOT_AVAILABLE, reason: 'FORM_CHANGED_AFTER_APPLY' };
    }
    const result = d.applyOperations(record.inverseOperations, { proposal: null, entity: record.entity, undo: true, proposalId: record.proposalId }) || {};
    if (!result.applied || result.applied.length === 0) {
      return { status: 'rejected', code: result.rejected?.[0]?.code || ERROR_CODES.UNDO_NOT_AVAILABLE };
    }
    record.status = PROPOSAL_STATUS.UNDONE;
    generationRef.current += 1;
    return { status: 'undone', proposalId: record.proposalId, revisionRestored: record.revisionBefore };
  }, [lastUndoable, getRevision]);

  /** Save boundary: o formulário persistiu pelo fluxo canônico → histórico
   * de undo daquela instância é finalizado (undo do contrato não desfaz backend). */
  const notifyCanonicalSave = useCallback((formInstanceId) => {
    const inst = formInstanceId || instanceOf(activeFormRef.current);
    historyRef.current.delete(inst);
    generationRef.current += 1;
  }, []);

  const value = useMemo(() => ({
    registerAssistForm, unregisterAssistForm, getActiveForm, activeFormId,
    getRevision, getFocus, currentGeneration, captureSendContext, supersedePending,
    proposalFromFillForm, evaluateProposal, resolveCurrentValue, applyProposal,
    undoLastAppliedProposal, canUndo, notifyCanonicalSave, supportsContract,
  }), [registerAssistForm, unregisterAssistForm, getActiveForm, activeFormId,
    getRevision, getFocus, currentGeneration, captureSendContext, supersedePending,
    proposalFromFillForm, evaluateProposal, resolveCurrentValue, applyProposal,
    undoLastAppliedProposal, canUndo, notifyCanonicalSave, supportsContract]);

  return (
    <AssistFormBridgeContext.Provider value={value}>
      {children}
    </AssistFormBridgeContext.Provider>
  );
};
